import React, { useEffect, useMemo, useState } from 'react';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import AccountReceivableForm from '../components/AccountReceivableForm';
import BulkActionsDropdown from '../components/BulkActionsDropdown';
import PaymentModal from '../components/PaymentModal';
import RefundModal from '../components/RefundModal';
import RowActionsDropdown from '../components/RowActionsDropdown';
import RecurrenceScopeModal from '../components/RecurrenceScopeModal';
import { accountReceivableApi, payerApi, paymentMethodApi } from '../services/api';
import { AccountReceivable, CreateAccountReceivableRequest, UpdateAccountReceivableRequest, Payer, PaymentMethod, RecurrenceScope } from '../types';
import PeriodInput, { periodToStartIso, periodToEndIso, isoToPeriod } from '../components/PeriodInput';
import Alert from '../components/Alert';
import PageSuggestion from '../components/PageSuggestion';
import { formatDateAlt } from '../utils/dateUtils';

const AccountsReceivable: React.FC = () => {
    const [items, setItems] = useState<AccountReceivable[]>([]);
    const [filtered, setFiltered] = useState<AccountReceivable[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [filterPayerId, setFilterPayerId] = useState<string>('');
    const [filterPaymentMethodId, setFilterPaymentMethodId] = useState<string>('');
    const [filterRecurrence, setFilterRecurrence] = useState<string>('');
    const [filterStatus, setFilterStatus] = useState<string>('');
    const [payers, setPayers] = useState<Payer[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const defaultPeriod = useMemo(() => isoToPeriod(null), []);
    const [filterStartPeriod, setFilterStartPeriod] = useState<string>(defaultPeriod);
    const [filterEndPeriod, setFilterEndPeriod] = useState<string>(defaultPeriod);
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<AccountReceivable | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [confirmMode, setConfirmMode] = useState<'single' | 'bulk'>('single');
    const [itemToDelete, setItemToDelete] = useState<AccountReceivable | null>(null);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
    const [isRecurrenceScopeModalOpen, setIsRecurrenceScopeModalOpen] = useState(false);
    const [recurrenceScopeAction, setRecurrenceScopeAction] = useState<'delete' | 'update' | null>(null);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const [pendingFormData, setPendingFormData] = useState<UpdateAccountReceivableRequest | null>(null);
    const [sortColumn, setSortColumn] = useState<string>('');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
    const [compactView, setCompactView] = useState<boolean>(false);

    const itemsPerPage = compactView ? 50 : 10;

    useEffect(() => {
        fetchItems();
    }, []);

    useEffect(() => {
        const loadLookups = async () => {
            const results = await Promise.allSettled([
                payerApi.getAll(),
                paymentMethodApi.getAll(),
            ]);
            const [payersRes, pmRes] = results;
            if (payersRes.status === 'fulfilled') setPayers(payersRes.value.data || []);
            if (pmRes.status === 'fulfilled') setPaymentMethods(pmRes.value.data || []);
        };
        loadLookups();
    }, []);

    useEffect(() => {
        let filteredItems = items.filter((i) =>
            i.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Apply sorting
        if (sortColumn && sortDirection) {
            filteredItems = [...filteredItems].sort((a, b) => {
                let aValue = a[sortColumn as keyof AccountReceivable];
                let bValue = b[sortColumn as keyof AccountReceivable];

                // Handle different data types
                if (sortColumn === 'amount') {
                    aValue = Number(aValue);
                    bValue = Number(bValue);
                } else if (sortColumn === 'dueDate' || sortColumn === 'receiptDate') {
                    aValue = new Date(aValue as string).getTime();
                    bValue = new Date(bValue as string).getTime();
                } else if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = (bValue as string).toLowerCase();
                }

                if (sortDirection === 'asc') {
                    return (aValue as any) < (bValue as any) ? -1 : (aValue as any) > (bValue as any) ? 1 : 0;
                } else {
                    return (aValue as any) > (bValue as any) ? -1 : (aValue as any) < (bValue as any) ? 1 : 0;
                }
            });
        }

        setFiltered(filteredItems);
        setCurrentPage(1);
    }, [searchTerm, items, sortColumn, sortDirection]);

    const fetchItems = async () => {
        try {
            setLoading(true);
            setLoadError(null);
            const toUtcBoundary = (iso: string, endOfDay: boolean) => {
                const d = new Date(iso);
                const y = d.getUTCFullYear();
                const m = d.getUTCMonth();
                const day = d.getUTCDate();
                const utc = endOfDay ? new Date(Date.UTC(y, m, day, 23, 59, 59, 999)) : new Date(Date.UTC(y, m, day, 0, 0, 0, 0));
                return utc.toISOString();
            };
            const startIso = periodToStartIso(filterStartPeriod);
            const endIso = periodToEndIso(filterEndPeriod);
            const params: Record<string, any> = {
                dueDateStart: toUtcBoundary(startIso, false),
                dueDateEnd: toUtcBoundary(endIso, true),
            };
            if (filterPayerId) params.payerId = filterPayerId;
            if (filterPaymentMethodId) params.paymentMethodId = filterPaymentMethodId;
            if (filterRecurrence) params.recurrence = filterRecurrence;
            if (filterStatus) params.status = filterStatus;
            const response = await accountReceivableApi.getAll(params);
            setItems(response.data || []);
        } catch (e: any) {
            setItems([]);
            const friendly = e?.response?.data?.translatedMessage || 'Não foi possível carregar as contas a receber.';
            setLoadError(friendly);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (data: UpdateAccountReceivableRequest) => {
        if (!editingItem) return;

        try {
            setSubmitError(null);
            await accountReceivableApi.update(editingItem.id, data);
            await fetchItems();
            setIsModalOpen(false);
            setEditingItem(null);
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Falha ao atualizar a conta a receber.';
            setSubmitError(friendly);
        }
    };

    const handleCreate = async (data: CreateAccountReceivableRequest) => {
        try {
            setSubmitError(null);
            await accountReceivableApi.create(data);
            await fetchItems();
            setIsModalOpen(false);
            setEditingItem(null);
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Falha ao criar a conta a receber.';
            setSubmitError(friendly);
        }
    };

    const handleEdit = (item: AccountReceivable) => {
        setEditingItem(item);
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingItem(null);
        setIsModalOpen(true);
        setSubmitError(null);
    };

    const handleToggleSelect = (id: string, checked: boolean) => {
        setSelectedIds((prev) => {
            if (checked) {
                return prev.indexOf(id) === -1 ? prev.concat(id) : prev;
            }
            return prev.filter((x) => x !== id);
        });
    };

    const handleToggleSelectAll = (checked: boolean) => {
        if (checked) setSelectedIds(items.map(i => i.id));
        else setSelectedIds([]);
    };

    const handleDeleteOne = async (item: AccountReceivable) => {
        try {
            await accountReceivableApi.delete(item.id);
            await fetchItems();
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Falha ao excluir a conta a receber.';
            setLoadError(friendly);
        }
    };

    const handleDeleteSelected = async () => {
        try {
            for (const id of selectedIds) {
                await accountReceivableApi.delete(id);
            }
            setSelectedIds([]);
            await fetchItems();
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Falha ao excluir contas selecionadas.';
            setLoadError(friendly);
        }
    };

    const requestDeleteOne = (item: AccountReceivable) => {
        // Não permitir exclusão de contas recebidas
        if (item.status === 'received') {
            setLoadError('Não é possível excluir contas que já foram recebidas. Estorne o recebimento primeiro.');
            return;
        }

        const deleteAction = () => {
            setConfirmMode('single');
            setItemToDelete(item);
            setIsConfirmOpen(true);
        };
        checkRecurrenceAndExecute(item, 'delete', deleteAction);
    };

    const requestDeleteSelected = () => {
        // Verificar se alguma conta selecionada está recebida
        const selectedItems = items.filter(item => selectedIds.includes(item.id));
        const hasReceivedItems = selectedItems.some(item => item.status === 'received');

        if (hasReceivedItems) {
            setLoadError('Não é possível excluir contas que já foram recebidas. Remova as contas recebidas da seleção ou estorne o recebimento primeiro.');
            return;
        }

        setConfirmMode('bulk');
        setItemToDelete(null);
        setIsConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            if (confirmMode === 'single' && itemToDelete) {
                await accountReceivableApi.delete(itemToDelete.id);
            } else if (confirmMode === 'bulk') {
                for (const id of selectedIds) {
                    await accountReceivableApi.delete(id);
                }
                setSelectedIds([]);
            }
            setIsConfirmOpen(false);
            setItemToDelete(null);
            await fetchItems();
        } catch (e) {
            setLoadError('Falha ao excluir.');
            setIsConfirmOpen(false);
        }
    };

    const handleCloseConfirm = () => {
        setIsConfirmOpen(false);
        setItemToDelete(null);
    };

    const handleBulkPay = () => {
        setIsPaymentModalOpen(true);
    };

    const handleBulkRefund = () => {
        setIsRefundModalOpen(true);
    };

    const handleConfirmPayment = async (paymentDate: string, useIndividualDueDates?: boolean) => {
        try {
            // Filtrar apenas contas que NÃO estão recebidas
            const unreceivedIds = selectedIds.filter(id => {
                const item = items.find(i => i.id === id);
                return item && item.status !== 'received';
            });

            if (unreceivedIds.length === 0) {
                setLoadError('Nenhuma conta pendente foi selecionada para marcar como recebida.');
                setIsPaymentModalOpen(false);
                return;
            }

            for (const id of unreceivedIds) {
                let finalReceiptDate = paymentDate;
                if (useIndividualDueDates) {
                    const item = items.find(i => i.id === id);
                    if (item) {
                        finalReceiptDate = item.dueDate;
                    }
                }
                await accountReceivableApi.receive(id, { receiptDate: finalReceiptDate });
            }
            setSelectedIds([]);
            setIsPaymentModalOpen(false);
            await fetchItems();
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Falha ao marcar como recebido.';
            setLoadError(friendly);
            setIsPaymentModalOpen(false);
        }
    };

    const getSelectedItemsDueDate = () => {
        if (selectedIds.length === 0) return null;
        const selectedItems = items.filter(item => selectedIds.includes(item.id));
        if (selectedItems.length === 0) return null;

        // If all selected items have the same due date, return it
        const firstDueDate = selectedItems[0].dueDate;
        const allSameDueDate = selectedItems.every(item => item.dueDate === firstDueDate);

        return allSameDueDate ? firstDueDate : null;
    };

    const handleConfirmRefund = async () => {
        try {
            // Filtrar apenas contas que ESTÃO recebidas
            const receivedIds = selectedIds.filter(id => {
                const item = items.find(i => i.id === id);
                return item && item.status === 'received';
            });

            if (receivedIds.length === 0) {
                setLoadError('Nenhuma conta recebida foi selecionada para estornar.');
                setIsRefundModalOpen(false);
                return;
            }

            for (const id of receivedIds) {
                await accountReceivableApi.refund(id);
            }
            setSelectedIds([]);
            setIsRefundModalOpen(false);
            await fetchItems();
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Falha ao estornar recebimento.';
            setLoadError(friendly);
            setIsRefundModalOpen(false);
        }
    };

    const openSingleReceive = (item: AccountReceivable) => {
        setSelectedIds([item.id]);
        setIsPaymentModalOpen(true);
    };

    const openSingleRefund = (item: AccountReceivable) => {
        setSelectedIds([item.id]);
        setIsRefundModalOpen(true);
    };

    const checkRecurrenceAndExecute = (item: AccountReceivable, action: 'delete' | 'update', actionFn: () => void) => {
        if (item.recurrenceGroupIdentifier) {
            setRecurrenceScopeAction(action);
            setPendingAction(() => actionFn);
            setIsRecurrenceScopeModalOpen(true);
        } else {
            actionFn();
        }
    };

    const handleRecurrenceScopeConfirm = async (scope: RecurrenceScope) => {
        if (recurrenceScopeAction === 'update' && pendingFormData) {
            const dataWithScope = { ...pendingFormData, recurrenceScope: scope };
            try {
                setSubmitError(null);
                await accountReceivableApi.update(editingItem!.id, dataWithScope);
                await fetchItems();
                setIsModalOpen(false);
                setEditingItem(null);
            } catch (e: any) {
                setSubmitError('Falha ao atualizar a conta a receber.');
            }
        } else if (pendingAction) {
            pendingAction();
        }

        // Close the recurrence scope modal and reset state
        setIsRecurrenceScopeModalOpen(false);
        setPendingAction(null);
        setRecurrenceScopeAction(null);
        setPendingFormData(null);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
        setSubmitError(null);
    };

    const handleSort = (column: string, direction: 'asc' | 'desc' | null) => {
        setSortColumn(column);
        setSortDirection(direction);
    };

    const columns = useMemo(() => {
        if (compactView) {
            return [
                { key: 'description', label: 'Descrição', sortable: true },
                { key: 'amount', label: 'Valor', sortable: true, render: (v: number) => (v / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
            ];
        }
        return [
        { key: 'description', label: 'Descrição', sortable: true },
        { key: 'amount', label: 'Valor', sortable: true, render: (v: number) => (v / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
        { key: 'recurrence', label: 'Recorrência', sortable: true, render: (v: string) => ({ once: 'Única', daily: 'Diária', weekly: 'Semanal', monthly: 'Mensal', yearly: 'Anual' }[v] || v) },
        {
            key: 'status', label: 'Status', sortable: true, render: (v: string) => {
                if (v === 'received') return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Recebido</span>;
                if (v === 'overdue') return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Vencida</span>;
                if (v === 'cancelled') return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">Cancelado</span>;
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Pendente</span>;
            }
        },
        { key: 'dueDate', label: 'Vencimento', sortable: true, render: (v: string) => formatDateAlt(v) },
        { key: 'receiptDate', label: 'Recebimento', sortable: true, render: (v: string | null) => formatDateAlt(v) },
        ];
    }, [compactView]);

    const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);

    // Totalizadores
    const totalPending = useMemo(() => {
        const sum = filtered
            .filter(i => i.status === 'pending' || i.status === 'overdue')
            .reduce((acc, i) => acc + i.amount, 0);
        return (sum / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }, [filtered]);

    const totalReceived = useMemo(() => {
        const sum = filtered
            .filter(i => i.status === 'received')
            .reduce((acc, i) => acc + i.amount, 0);
        return (sum / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }, [filtered]);

    const totalGeneral = useMemo(() => {
        const sum = filtered.reduce((acc, i) => acc + i.amount, 0);
        return (sum / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }, [filtered]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-secondary-600">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {loadError && (<Alert type="error" title="Não foi possível concluir" message={loadError} />)}
            <div className="bg-white p-4 rounded-lg border border-secondary-200">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="w-full sm:w-auto sm:min-w-[120px]">
                        <PeriodInput
                            label="Data inicial (MM/AAAA)"
                            name="dueDateStart"
                            value={filterStartPeriod}
                            onChange={(v) => {
                                setFilterStartPeriod(v);
                                setFilterEndPeriod(v);
                            }}
                            compact
                        />
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[120px]">
                        <PeriodInput label="Data final (MM/AAAA)" name="dueDateEnd" value={filterEndPeriod} onChange={setFilterEndPeriod} compact />
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[140px]">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Pagador</label>
                        <select className="input-field" value={filterPayerId} onChange={(e) => setFilterPayerId(e.target.value)}>
                            <option value="">Todos</option>
                            {payers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[140px]">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Método de Pagamento</label>
                        <select className="input-field" value={filterPaymentMethodId} onChange={(e) => setFilterPaymentMethodId(e.target.value)}>
                            <option value="">Todos</option>
                            {paymentMethods.map(pm => <option key={pm.id} value={pm.id}>{pm.description}</option>)}
                        </select>
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[120px]">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Recorrência</label>
                        <select className="input-field" value={filterRecurrence} onChange={(e) => setFilterRecurrence(e.target.value)}>
                            <option value="">Todas</option>
                            <option value="once">Única</option>
                            <option value="daily">Diária</option>
                            <option value="weekly">Semanal</option>
                            <option value="monthly">Mensal</option>
                            <option value="yearly">Anual</option>
                        </select>
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[120px]">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Status</label>
                        <select className="input-field" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                            <option value="">Todos</option>
                            <option value="pending">Pendente</option>
                            <option value="received">Recebido</option>
                            <option value="cancelled">Cancelado</option>
                        </select>
                    </div>
                    <button onClick={fetchItems} className="btn-primary w-full sm:w-auto shrink-0 h-[42px]">Filtrar</button>
                </div>
            </div>
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-secondary-900">Contas a Receber</h1>
                    <p className="text-secondary-600">Listagem com busca, paginação e edição</p>
                    <PageSuggestion>
                        Registre valores esperados de clientes ou outras fontes e acompanhe vencimentos, recebimentos e estornos no mesmo lugar.
                    </PageSuggestion>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        className="btn-secondary"
                        onClick={() => setCompactView((v) => !v)}
                        aria-pressed={compactView}
                    >
                        {compactView ? 'Visão detalhada' : 'Visão compacta'}
                    </button>
                    <BulkActionsDropdown
                        selectedCount={selectedIds.length}
                        onDelete={requestDeleteSelected}
                        onPay={handleBulkPay}
                        onRefund={handleBulkRefund}
                        showPay={items.filter(i => selectedIds.includes(i.id)).some(i => i.status !== 'received')}
                        showRefund={items.filter(i => selectedIds.includes(i.id)).some(i => i.status === 'received')}
                    />
                    <button onClick={handleAddNew} className="btn-primary">Adicionar Conta a Receber</button>
                </div>
            </div>

            {/* Totalizadores */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="text-sm font-medium text-yellow-800">A Receber</div>
                    <div className="mt-1 text-2xl font-bold text-yellow-900">{totalPending}</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-sm font-medium text-green-800">Recebido</div>
                    <div className="mt-1 text-2xl font-bold text-green-900">{totalReceived}</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-sm font-medium text-blue-800">Total</div>
                    <div className="mt-1 text-2xl font-bold text-blue-900">{totalGeneral}</div>
                </div>
            </div>

            <DataTable
                data={paginatedData}
                columns={columns}
                searchPlaceholder="Buscar por descrição..."
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                selectable
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onToggleSelectAll={handleToggleSelectAll}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                dense={compactView}
                actionsRenderer={(item) => (
                    <RowActionsDropdown
                        onEdit={() => handleEdit(item)}
                        onDelete={() => requestDeleteOne(item)}
                        onPay={() => openSingleReceive(item)}
                        onRefund={() => openSingleRefund(item)}
                        payLabel="Marcar como Recebido"
                        refundLabel="Estornar Recebimento"
                        editLabel="Editar"
                        deleteLabel="Excluir"
                        showPay={item.status !== 'received'}
                        showRefund={item.status === 'received'}
                        showDelete={item.status !== 'received'}
                        isReadOnly={item.status === 'received'}
                    />
                )}
            />

            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editingItem?.status === 'received' ? 'Visualizar Conta a Receber' : editingItem ? 'Editar Conta a Receber' : 'Adicionar Conta a Receber'}
            >
                {submitError && (
                    <div className="mb-3 p-3 rounded-md bg-red-50 border border-red-200 text-red-700">
                        {submitError}
                    </div>
                )}
                <AccountReceivableForm
                    accountReceivable={editingItem}
                    readOnly={editingItem?.status === 'received'}
                    onSubmit={(data) => {
                        // Check if amount changed and item is recurring
                        if (editingItem && editingItem.recurrenceGroupIdentifier) {
                            const amountChanged = editingItem.amount !== (data.amount * 100);
                            if (amountChanged && !data.recurrenceScope) {
                                setPendingFormData(data as UpdateAccountReceivableRequest);
                                setRecurrenceScopeAction('update');
                                setIsRecurrenceScopeModalOpen(true);
                                return;
                            }
                        }
                        editingItem ? handleUpdate(data as UpdateAccountReceivableRequest) : handleCreate(data as CreateAccountReceivableRequest);
                    }}
                    onCancel={handleCloseModal}
                />
            </Modal>

            {/* Confirmation Modal */}
            <Modal
                isOpen={isConfirmOpen}
                onClose={handleCloseConfirm}
                title={confirmMode === 'single' ? 'Confirmar Exclusão' : 'Confirmar Exclusão de Múltiplos'}
            >
                <div className="space-y-4">
                    <p className="text-secondary-700">
                        {confirmMode === 'single'
                            ? `Tem certeza que deseja excluir a conta a receber "${itemToDelete?.description}"?`
                            : `Tem certeza que deseja excluir as ${selectedIds.length} contas a receber selecionadas?`}
                    </p>
                    <div className="flex justify-end space-x-2 pt-2">
                        <button onClick={handleCloseConfirm} className="btn-secondary">Cancelar</button>
                        <button onClick={handleConfirmDelete} className="btn-primary bg-red-600 hover:bg-red-700 border-red-600">Excluir</button>
                    </div>
                </div>
            </Modal>

            {/* Payment Modal */}
            <PaymentModal
                isOpen={isPaymentModalOpen}
                onClose={() => setIsPaymentModalOpen(false)}
                onConfirm={handleConfirmPayment}
                selectedCount={selectedIds.length}
                dueDate={getSelectedItemsDueDate() || undefined}
                title="Marcar como Recebido"
            />

            {/* Refund Modal */}
            <RefundModal
                isOpen={isRefundModalOpen}
                onClose={() => setIsRefundModalOpen(false)}
                onConfirm={handleConfirmRefund}
                selectedCount={selectedIds.length}
                title="Estornar Recebimento"
            />

            {/* Recurrence Scope Modal */}
            <RecurrenceScopeModal
                isOpen={isRecurrenceScopeModalOpen}
                onClose={() => {
                    setIsRecurrenceScopeModalOpen(false);
                    setPendingAction(null);
                    setRecurrenceScopeAction(null);
                    setPendingFormData(null);
                }}
                onConfirm={handleRecurrenceScopeConfirm}
                title={recurrenceScopeAction === 'delete' ? 'Excluir Cobrança Recorrente' : 'Atualizar Cobrança Recorrente'}
                description={recurrenceScopeAction === 'delete'
                    ? 'Esta cobrança faz parte de um conjunto de cobranças recorrentes. Escolha o escopo da exclusão:'
                    : 'Esta cobrança faz parte de um conjunto de cobranças recorrentes. Escolha o escopo da atualização:'
                }
                itemDescription={itemToDelete?.description || editingItem?.description}
            />
        </div>
    );
};

export default AccountsReceivable;










